/**
 * INCSUBAddCustomTransaction Component
 * 
 * This component allows users to add or update custom transactions. It provides inputs for
 * transaction details like invoice ID, transaction amount, payment mode, and remarks. 
 * It also allows users to choose a date and time for the transaction.
 * 
 * Features:
 * - Input fields for transaction details (Invoice ID, Amount, Payment Mode, etc.).
 * - Date picker for selecting transaction date.
 * - Buttons for adding or updating transactions.
 * - Handles validation for required fields.
 * - Integration with Redux to update global state with new or updated transactions.
 * 
 * Props:
 * - `onClick`: Function to handle closing or cancellation of the transaction form.
 * - `selectedTxn`: Optional pre-selected transaction data to edit (for update).
 * 
 * State:
 * - `txnData`: Stores the transaction data in the form.
 * - `isEmptyField`: Tracks if any required fields are empty.
 * - `loader`: Shows a loading indicator during API requests.
 * 
 * Functions:
 * - `addCustomTxn`: Adds a new custom transaction after validating the form fields.
 * - `updateTransaction`: Updates an existing custom transaction if there are changes.
 * - `findUpdatedFields`: Compares original and updated data and returns the fields that have changed.
 * - `parseCustomDate`: Converts the string date-time to a Date object.
 * 
 * Redux:
 * - Uses Redux to store and update the transaction list in the global state (`setRevenueData`).
 * 
 * API Calls:
 * - `postCustomTxn`: Makes an API request to create a new custom transaction.
 * - `updateCustomTxn`: Makes an API request to update an existing transaction.
 * 
 * @returns {JSX.Element} Form for adding or updating a custom transaction.
 */
import React, { useEffect, useState } from 'react';
import './AddCustomTransaction.css'; // Styles for the Add Custom Transaction form
import INCSUBTrasactionInputComponent from './TrasactionInputComponent'; // Custom input components for transactions
import INCSUBButton from '../common/Button/INCSUBButton'; // Reusable Button Component
import { useDispatch, useSelector } from 'react-redux'; // Redux hooks for state management
import { useNavigate } from 'react-router'; // Hook to navigate between routes
import { toast } from 'react-toastify'; // Notification library for displaying errors
import { postCustomTxn, updateCustomTxn } from '../../services/revenueServices'; // API calls for transactions
import DatePicker from 'react-datepicker'; // Date picker for selecting transaction date
import { setRevenueData } from '../../features/revenue/revenueSlice'; // Redux action for updating transaction list
import _ from "lodash"; // Utility library for deep comparisons (used in updating transactions)

// INCSUBAddCustomTransaction component definition
function INCSUBAddCustomTransaction({ onClick, selectedTxn, txnType }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const revenueData = useSelector((state) => state.revenue.revenueData); // Accessing Redux state for revenue data

    // Function to format the transaction date
    const formatDateTime = (dateTime) => {
        try {
            const date = new Date(dateTime);
            const options = { year: "numeric", month: "2-digit", day: "2-digit" };
            const formattedDateSplit = date.toLocaleDateString("en-GB", options).replace(/\//g, "-").split('-');
            const formattedDate = `${formattedDateSplit[2]}-${formattedDateSplit[1]}-${formattedDateSplit[0]}`;
            const hours = date.getHours();
            const minutes = date.getMinutes().toString().padStart(2, "0");
            const period = hours >= 12 ? "PM" : "AM";
            const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${"00"}`;
            return `${formattedDate} ${formattedTime}`;
        } catch (err) {
            console.log(err)
        }

    };

    // State for transaction data and form
    const [txnData, setTxnData] = useState({
        invoiceId: "",
        transactionAmount: '',
        paymentMode: "",
        transactionId: "",
        transactionDate: formatDateTime(new Date()), // Initialize with current date
        transactionType: "",
        transactionRemark: "",
        userId: JSON.parse(window.localStorage.getItem('userData')).userId // Get user data from local storage
    });

    // If selectedTxn is passed, pre-fill the form with the selected transaction data
    useEffect(() => {
        if (selectedTxn) {
            setTxnData(selectedTxn);
        }
    }, [selectedTxn]);

    const [isEmptyField, setIsEmptyField] = useState(false); // Track if any required field is empty
    const [loader, setLoader] = useState(false); // Loader state to show loading indicator

    // Function to add a custom transaction
    const addCustomTxn = async () => {
        setLoader(true);
        const user = JSON.parse(window.localStorage.getItem('userData'));

        // Validate that all required fields are filled
        const isInvalid = [
            txnData.invoiceId,
            txnData.transactionAmount,
            txnData.paymentMode,
            txnData.transactionId,
            txnData.transactionDate,
            txnData.transactionType,
            txnData.transactionRemark,
            txnData.userId
        ].some(field => field === '' || field === null || field === undefined);

        if (isInvalid) {
            setIsEmptyField(true);
            setLoader(false);
            return;
        }

        try {
            const postTxnData = await postCustomTxn(txnData);
            if (postTxnData.status === 201) {
                // On success, update the Redux state and close the form
                const updatedData = revenueData.transactionDtoList;
                if (txnData?.transactionType === 'CREDIT') {
                    const updatedIncome = parseFloat(revenueData.profitLossDto.totalIncome) + parseFloat(txnData.transactionAmount);
                    const totalExpense = parseFloat(revenueData.profitLossDto.totalExpense);

                    dispatch(setRevenueData({
                        ...revenueData,
                        profitLossDto: {
                            ...revenueData.profitLossDto,
                            totalIncome: updatedIncome + ".00",
                            totalProfit: (updatedIncome + totalExpense) + ".00"
                        },
                        transactionDtoList: txnType === txnData.transactionType ? [{
                            ...txnData,
                            customTransaction: true,
                            transactionByUserFullName: user.userFullName,
                            transactionByUserNumber: user.userMobileNumber,
                            userImage: user.userImage
                        }, ...updatedData] : [...updatedData]
                    }));
                } else {
                    const updatedExpense = parseFloat(revenueData.profitLossDto.totalExpense) + parseFloat(txnData.transactionAmount);
                    const totalIncome = parseFloat(revenueData.profitLossDto.totalIncome);

                    dispatch(setRevenueData({
                        ...revenueData,
                        profitLossDto: {
                            ...revenueData.profitLossDto,
                            totalExpense: updatedExpense + ".00",
                            totalProfit: (totalIncome + updatedExpense) + ".00"
                        },
                        transactionDtoList: txnType === txnData.transactionType ? [{
                            ...txnData,
                            customTransaction: true,
                            transactionByUserFullName: user.userFullName,
                            transactionByUserNumber: user.userMobileNumber,
                            userImage: user.userImage
                        }, ...updatedData] : [...updatedData]
                    }));
                }
                onClick(false);
            } else if (postTxnData.status === 401) {
                // Handle unauthorized access
                window.localStorage.setItem('token', null);
                window.localStorage.setItem('userData', null);
                navigate('/login');
            } else {
                toast.error(postTxnData.data.message);
            }
        } catch (err) {
            toast.error("Something went wrong");
        } finally {
            setLoader(false);
        }
    };

    // Function to find the fields that have been updated
    const findUpdatedFields = (original, updated) => {
        let updatedData = {};
        Object.keys(original).forEach(key => {
            if (original[key] !== updated[key]) {
                updatedData[key] = updated[key];
            }
        });
        if (Object.keys(updatedData).length > 0) {
            updatedData["transactionInfoId"] = original["transactionInfoId"];
        }
        return Object.keys(updatedData).length > 0 ? updatedData : false;
    };

    // Function to update an existing transaction
    const updateTransaction = async () => {
        const areTxnDataEqual = _.isEqual(selectedTxn, txnData);
        if (areTxnDataEqual) {
            toast.warning("No change in previous data");
            return;
        }

        setLoader(true);
        const user = JSON.parse(window.localStorage.getItem('userData'));

        // Validate that all required fields are filled
        const isInvalid = [
            txnData.invoiceId,
            txnData.transactionAmount,
            txnData.paymentMode,
            txnData.transactionId,
            txnData.transactionDate,
            txnData.transactionType,
            txnData.transactionRemark,
        ].some(field => field === '' || field === null || field === undefined);

        if (isInvalid) {
            setIsEmptyField(true);
            setLoader(false);
            return;
        }

        const updatedData = findUpdatedFields(selectedTxn, txnData);
        try {
            const postTxnData = await updateCustomTxn(updatedData);

            if (postTxnData.status === 200) {
                // On success, update the Redux state with the updated transaction
                const updatedList = revenueData.transactionDtoList.map(txn =>
                    txn.transactionInfoId === txnData.transactionInfoId
                        ? { ...txn, ...txnData }
                        : txn
                );
                if (selectedTxn?.transactionType === 'CREDIT') {
                    const tempTxn = selectedTxn?.transactionAmount
                    const updatedIncome = (parseFloat(revenueData.profitLossDto.totalIncome) - parseFloat(tempTxn)) + parseFloat(txnData.transactionAmount);
                    const totalExpense = parseFloat(revenueData.profitLossDto.totalExpense);
                    dispatch(setRevenueData({
                        ...revenueData,
                        profitLossDto: {
                            ...revenueData.profitLossDto,
                            totalIncome: updatedIncome + ".00",
                            totalProfit: (updatedIncome - totalExpense) + ".00"
                        },
                        transactionDtoList: updatedList
                    }));
                }else{
                    const tempTxn = selectedTxn?.transactionAmount
                    const updatedExpense = (parseFloat(revenueData.profitLossDto.totalExpense) - parseFloat(tempTxn)) + parseFloat(txnData.transactionAmount);
                    const totalIncome = parseFloat(revenueData.profitLossDto.totalIncome);
                    dispatch(setRevenueData({
                        ...revenueData,
                        profitLossDto: {
                            ...revenueData.profitLossDto,
                            totalExpense: updatedExpense + ".00",
                            totalProfit: (totalIncome - updatedExpense) + ".00"
                        },
                        transactionDtoList: updatedList
                    }));
                }

                // dispatch(setRevenueData({
                //     ...revenueData,
                //     transactionDtoList: updatedList
                // }));

                onClick(false);
            } else if (postTxnData.status === 301) {
                toast.error("Network Error");
            } else if (postTxnData.status === 500) {
                toast.error("Internal server error");
            } else if (postTxnData.status === 401) {
                // Handle unauthorized access
                window.localStorage.setItem('token', null);
                window.localStorage.setItem('userData', null);
                navigate('/login');
            } else {
                toast.error(postTxnData.data.message);
            }
        } catch (err) {
            toast.error("Something went wrong");
        } finally {
            setLoader(false);
        }
    };

    function parseCustomDate(dateString) {
        try {
            const [datePart, timePart, period] = dateString.split(" ");
            const [year, month, day] = datePart.split("-").map(Number);
            const [hourStr, minuteStr] = timePart.split(":");
            let hour = Number(hourStr);
            const minute = Number(minuteStr);

            // Convert to 24-hour format
            if (period === "PM" && hour !== 12) hour += 12;
            if (period === "AM" && hour === 12) hour = 0;

            return new Date(year, month - 1, day, hour, minute);
        } catch (err) {
            toast.error("Data format is wrong")
        }
    }
    // Helper function to check if a string is a number
    const isNumber = (e) => {
        if (e === '') {
            return true;
        }
        return !isNaN(e) && !isNaN(parseFloat(e));
    }

    return (
        <div className='INCSUBAddCustomTransaction_ctn'>
            <div className='INCSUBAddCustomTransaction_card'>
                <div className='INCSUBAddCustomTransaction_card_heading_crossicon'>
                    <div className='INCSUBAddCustomTransaction_card_heading'>{selectedTxn ? "Update" : "Add"} Custom Transaction</div>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" onClick={() => {
                        onClick(false)
                    }}>
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M3.46318 3.47683C3.77048 3.16963 4.1872 2.99705 4.62172 2.99705C5.05624 2.99705 5.47296 3.16963 5.78026 3.47683L11.9957 9.69231L18.2112 3.47683C18.3624 3.32032 18.5432 3.19548 18.7431 3.1096C18.9431 3.02372 19.1581 2.97852 19.3757 2.97662C19.5932 2.97473 19.809 3.01619 20.0104 3.09859C20.2118 3.18098 20.3948 3.30266 20.5486 3.45652C20.7025 3.61038 20.8242 3.79334 20.9065 3.99473C20.9889 4.19611 21.0304 4.41189 21.0285 4.62947C21.0266 4.84706 20.9814 5.06208 20.8955 5.26201C20.8097 5.46193 20.6848 5.64275 20.5283 5.79391L14.3128 12.0094L20.5283 18.2249C20.8268 18.5339 20.992 18.9479 20.9882 19.3775C20.9845 19.8072 20.8122 20.2182 20.5083 20.522C20.2045 20.8258 19.7935 20.9982 19.3639 21.0019C18.9342 21.0056 18.5203 20.8405 18.2112 20.542L11.9957 14.3265L5.78026 20.542C5.4712 20.8405 5.05727 21.0056 4.62762 21.0019C4.19796 20.9982 3.78696 20.8258 3.48314 20.522C3.17932 20.2182 3.00698 19.8072 3.00325 19.3775C2.99951 18.9479 3.16468 18.5339 3.46318 18.2249L9.67866 12.0094L3.46318 5.79391C3.15598 5.48661 2.9834 5.06989 2.9834 4.63537C2.9834 4.20085 3.15598 3.78413 3.46318 3.47683Z" fill="black" />
                    </svg>
                </div>
                <div className='INCSUBTransactionInput_ctn'>
                    <input className='INCSUBTransactionInput' placeholder={"*Enter Invoice Id"} value={txnData.invoiceId} onChange={(e) => setTxnData(() => ({ ...txnData, invoiceId: e.target.value }))} style={{ border: isEmptyField && txnData.invoiceId === '' ? "1px solid red" : null }} />
                </div>
                <div className='INCSUBAddCustomTransaction_amount_div'>
                    <div className='INCSUBAddCustomTransaction_amount_heading'>*Amount</div>
                    <div className='INCSUBAddCustomTransaction_amount_input_radios'>
                        <input className='INCSUBTransactionInput' placeholder={'₹Enter amount'} value={txnData.transactionAmount} onChange={(e) => { if (isNumber(e.target.value)) { setTxnData(() => ({ ...txnData, transactionAmount: e.target.value })) } }} style={{ width: '48%', height: '50px', border: isEmptyField && txnData.transactionAmount === '' ? "1px solid red" : null }} />
                        <div className='INCSUBAddCustomTransaction_amount_radios'>
                            <div className='INCSUBAddCustomTransaction_amount_radios1' style={{ color: isEmptyField && txnData.transactionType === '' ? "red" : null }}>
                                <input
                                    type="radio"
                                    value="DEBIT"
                                    checked={txnData.transactionType === 'DEBIT'}
                                    onChange={(e) => setTxnData(() => ({ ...txnData, transactionType: e.target.value }))}

                                />
                                Expense
                            </div>
                            <div className='INCSUBAddCustomTransaction_amount_radios1' style={{ color: isEmptyField && txnData.transactionType === '' ? "red" : null }}>
                                <input
                                    type="radio"
                                    value="CREDIT"
                                    checked={txnData.transactionType === 'CREDIT'}
                                    onChange={(e) => setTxnData(() => ({ ...txnData, transactionType: e.target.value }))}
                                />
                                Income
                            </div>
                        </div>
                    </div>
                </div>

                <div className='INCSUBAddCustomTransaction_paymenttype'>
                    <div className='INCSUBAddCustomTransaction_payment_type_divhold'>
                        <div className="INCSUBAddNotification_form_fullfeild_container half">
                            <label>*Payment Type</label>
                            <select placeholder="Select a category" value={txnData.paymentMode} onChange={(e) => setTxnData(() => ({ ...txnData, paymentMode: e.target.value }))} style={{ border: isEmptyField && txnData.paymentMode === '' ? "1px solid red" : null }}>
                                <option value={''} style={{ color: '#A6A6A6' }} disabled selected>Select a Type</option>
                                <option value={"Debit card"}>Debit card</option>
                                <option value={"Credit card"}>Credit card</option>
                                <option value={"Upi"}>Upi</option>
                                <option value={"Net banking"}>Net banking</option>
                                <option value={"Wallet"}>Wallet</option>
                            </select>
                        </div>
                    </div>
                    <div className='INCSUBAddCustomTransaction_payment_type_divhold'>
                        <div className='INCSUBAddCustomTransaction_amount_heading'>*Transaction ID</div>
                        <input className='INCSUBTransactionInput' placeholder={'Type here...'} style={{ width: '100%', height: '50px', border: isEmptyField && txnData.transactionId === '' ? "1px solid red" : null }} value={txnData.transactionId} onChange={(e) => setTxnData(() => ({ ...txnData, transactionId: e.target.value }))} />
                    </div>
                </div>
                <div className='INCSUBAddCustomTransaction_amount_div'>
                    <div className='INCSUBAddCustomTransaction_amount_heading'>*Date</div>
                    <DatePicker
                        placeholderText={"DD MM YYYY 00:00am"}
                        selected={txnData.transactionDate === "" ? null : parseCustomDate(txnData.transactionDate)}
                        onChange={(date) => {
                            const formatted = formatDateTime(date);

                            setTxnData((prev) => ({
                                ...prev,
                                transactionDate: formatted
                            }));
                        }}
                        timeInputLabel="Time:"
                        dateFormat="dd-MM-yyyy h:mm aa"
                        showTimeInput
                        maxDate={new Date()}
                        showIcon
                        icon={<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clip-path="url(#clip0_367_5814)">
                                <path d="M12.6097 2.71875H3.37491C2.64631 2.71875 2.05566 3.3094 2.05566 4.038V13.2727C2.05566 14.0013 2.64631 14.592 3.37491 14.592H12.6097C13.3383 14.592 13.9289 14.0013 13.9289 13.2727V4.038C13.9289 3.3094 13.3383 2.71875 12.6097 2.71875Z" stroke="black" stroke-width="1.31925" stroke-linecap="round" stroke-linejoin="round" />
                                <path d="M10.6299 1.40625V4.04475" stroke="black" stroke-width="1.31925" stroke-linecap="round" stroke-linejoin="round" />
                                <path d="M5.35449 1.40625V4.04475" stroke="black" stroke-width="1.31925" stroke-linecap="round" stroke-linejoin="round" />
                                <path d="M2.05566 6.67969H13.9289" stroke="black" stroke-width="1.31925" stroke-linecap="round" stroke-linejoin="round" />
                            </g>
                            <defs>
                                <clipPath id="clip0_367_5814">
                                    <rect width="15.831" height="15.831" fill="white" transform="translate(0.0771484 0.0859375)" />
                                </clipPath>
                            </defs>
                        </svg>}
                        className={isEmptyField && txnData.transactionDate === '' ? "error" : ''}
                    // className={"error"}
                    />
                </div>
                <div className='INCSUBAddCustomTransaction_amount_div'>
                    <div className='INCSUBAddCustomTransaction_amount_heading'>*Remark</div>
                    <input className='INCSUBTransactionInput' placeholder={'Type here...'} style={{ width: '100%', height: '50px', border: isEmptyField && txnData.transactionRemark === '' ? "1px solid red" : null }} value={txnData.transactionRemark} onChange={(e) => setTxnData(() => ({ ...txnData, transactionRemark: e.target.value }))} />
                </div>
                {!selectedTxn ? <div className='INCSUBAddCustomTransaction_button'>{!loader ? <INCSUBButton text={'Add Transaction'} clickFun={addCustomTxn} /> : <div className='INCSUBusrupdate_btn submit'> <div className="btnLoader" /></div>}

                </div> :
                    <div className='INCSUBAddCustomTransaction_button'>{!loader ? <INCSUBButton text={'Update Transaction'} clickFun={updateTransaction} /> : <div className='INCSUBusrupdate_btn submit'> <div className="btnLoader" /></div>}

                    </div>
                }
            </div>
        </div>
    )
}

export default INCSUBAddCustomTransaction
